import React from 'react'
import { useTranslation } from 'react-i18next'
import { KBTag, KBIcon } from '@/components/atoms'
import { cn } from '@/utils/tailwind.helper'
import type { EffectiveSlot } from '@/hooks/useBookingSlots'
import dayjs from '@wox/dayjs'

export interface SlotCardProps {
  slot: EffectiveSlot
  selected?: boolean
  disabled?: boolean
  timezone?: string
  onClick?: () => void
}

const SlotCard: React.FC<SlotCardProps> = ({
  slot,
  selected = false,
  disabled = false,
  timezone,
  onClick,
}) => {
  const { t } = useTranslation()

  const startTime = timezone
    ? dayjs(slot.start_at).tz(timezone).format('HH:mm')
    : dayjs(slot.start_at).format('HH:mm')
  const endTime = timezone
    ? dayjs(slot.end_at).tz(timezone).format('HH:mm')
    : dayjs(slot.end_at).format('HH:mm')
  const isPast = dayjs(slot.end_at).isBefore(dayjs())
  const isFull = !isPast && slot.remaining_capacity === 0
  const isNotAvailable = isPast || (!slot.is_available && !isFull)
  const isInteractive = !disabled && !isFull && !isNotAvailable

  return (
    <div
      className={cn(
        'tw-relative tw-rounded-lg tw-border tw-border-solid tw-p-3 tw-transition-all',
        // Interactive states
        isInteractive && 'tw-cursor-pointer',
        isInteractive &&
          !selected &&
          'hover:tw-border-primary-500 tw-border-gray-300 tw-bg-white hover:tw-shadow-md',
        // Selected state
        selected && 'tw-border-primary-500 tw-bg-primary-50 tw-border-2',
        // Full state
        isFull && 'tw-cursor-not-allowed tw-border-gray-300 tw-bg-white',
        // Not available state
        isNotAvailable &&
          'tw-cursor-not-allowed tw-border-gray-300 tw-bg-gray-100',
        // Disabled (form level)
        disabled &&
          !isFull &&
          !isNotAvailable &&
          'tw-cursor-not-allowed tw-border-gray-200 tw-bg-white'
      )}
      onClick={() => {
        if (isInteractive && onClick) {
          onClick()
        }
      }}
    >
      {/* Top-right: status tag or selected checkmark */}
      <div className="tw-absolute tw-right-2 tw-top-2">
        {selected ? (
          <KBIcon name="check" size={16} className="tw-text-primary-500" />
        ) : isFull ? (
          <KBTag color="red" className="!tw-m-0 tw-text-xs">
            {t('booking.slot.full')}
          </KBTag>
        ) : isNotAvailable ? (
          <KBTag className="!tw-m-0 tw-text-xs">
            {t('booking.slot.notAvailable')}
          </KBTag>
        ) : (
          <KBTag color="green" className="!tw-m-0 tw-text-xs">
            {t('booking.slot.available')}
          </KBTag>
        )}
      </div>

      <div className="tw-flex tw-flex-col tw-gap-1">
        <span
          className={cn(
            'tw-pr-16 tw-font-semibold',
            isFull || isNotAvailable ? 'tw-text-gray-400' : 'tw-text-gray-900'
          )}
        >
          {slot.name}
        </span>

        <span
          className={cn(
            isFull || isNotAvailable ? 'tw-text-gray-400' : 'tw-text-gray-600'
          )}
        >
          {startTime} - {endTime}
        </span>

        {slot.capacity > 1 && !isFull && (
          <span
            className={cn(
              'tw-text-xs',
              isNotAvailable ? 'tw-text-gray-400' : 'tw-text-gray-500'
            )}
          >
            {t('booking.slot.remaining', {
              count: slot.remaining_capacity,
            })}
          </span>
        )}

        {isNotAvailable && slot.unavailable_reason && (
          <span className="tw-text-xs tw-text-gray-400">
            {t('booking.slot.reason')}
            {slot.unavailable_reason === 'advance_limitation'
              ? t('booking.slot.unavailableAdvanceLimit')
              : slot.unavailable_reason === 'no_permission'
                ? t('booking.slot.unavailableNoPermission')
                : slot.unavailable_reason}
          </span>
        )}
      </div>
    </div>
  )
}

export default SlotCard
